import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL } from '@/dtos';

import { Organization } from './useGetOrganization.query';

const QUERY_KEY = 'GET_ORGANIZATIONS_HIERARCHY_QUERY';

const formatData = (organizations: Organization[]): Organization[] =>
  organizations.map(
    (organization: Organization) =>
      ({
        ...organization,
        id: toString(organization.id),
        ...(organization?.children?.length
          ? {
              children: formatData(organization.children),
            }
          : {}),
      }) as Organization
  );

interface Props {
  searchValue?: string;
  isSystemLevel?: boolean;
  isCompanyLevel?: boolean;
  partnerParentId?: string;
  distributorParentId?: string;
}

const useGetOrganizationHierarchyQuery = (props: Props) => {
  const { searchValue, isCompanyLevel, partnerParentId, distributorParentId } = props;
  const { isLoading, data } = useQuery(
    [QUERY_KEY, searchValue],
    () =>
      api.get(
        API_URL.ORGANIZATIONS_HIERARCHY,
        ...(searchValue ? [{ params: { search_value: searchValue } }] : [])
      ),
    { enabled: !isCompanyLevel && !!searchValue }
  );

  const searchedPartners = useMemo(() => {
    if (data?.data?.children?.length) {
      return formatData((data?.data?.children || []) as unknown as Organization[]) as Organization[];
    }

    return undefined;
  }, [data]);
  const searchedDistributors = useMemo(() => {
    if (searchedPartners && partnerParentId) {
      return formatData(
        searchedPartners.find((partner: Organization) => toString(partner.id) === partnerParentId)
          ?.children || []
      ) as Organization[];
    }

    return undefined;
  }, [partnerParentId, searchedPartners]);
  const searchedCompanies = useMemo(() => {
    if (searchedDistributors && distributorParentId) {
      return formatData(
        searchedDistributors.find(
          (distributor: Organization) => toString(distributor.id) === distributorParentId
        )?.children || []
      ) as Organization[];
    }

    return undefined;
  }, [distributorParentId, searchedDistributors]);

  return {
    searchedPartners,
    searchedDistributors,
    searchedCompanies,
    isLoading,
    data: data?.data,
  };
};

export default useGetOrganizationHierarchyQuery;
